<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import type { components } from "~/assets/api/openapi"

const operationStore = useOperationStore()
const { t } = useI18n()
const REASON_MAX_LENGTH = 1000

type ApplicationType = "RULE_APPROBATION" | "EU" | "THIRD_PARTY"

const schema = yup.object().shape({
  proofOfResponsibility: yup.boolean().required("Pflichtfeld"),

  providedProof: yup.string().when(["proofOfResponsibility"], {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),

  reason: yup.string().when(["proofOfResponsibility", "providedProof"], {
    is: (proofOfResponsibility: boolean, providedProof: string) => {
      return proofOfResponsibility === false || providedProof?.endsWith("Other")
    },
    then: (schema) => schema.required("Pflichtfeld"),
    otherwise: (schema) => schema.strip(),
  }),

  responsibilityDate: yup.string().required("Pflichtfeld"),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

const applicationType = computed<ApplicationType | undefined>(() => {
  const textKey = operationStore.findFormDataValueByTextKey<
    components["schemas"]["LocaleValue"]
  >("gsb.ah.applicationDataFields.applicationType")?.textKey

  const lastPart = textKey?.split(".").pop()
  return lastPart as ApplicationType | undefined
})

const isReasonFieldEnabled = computed<boolean>(
  () =>
    state.proofOfResponsibility === false ||
    state.providedProof?.endsWith("Other") === true
)

const providedProofOptionsByApplicationType = {
  EU: [
    {
      label: `Erklärung Tätigkeitsort ${t("custom.common.federal-state")} abgegeben`,
      value: "euDeclaration",
    },
    {
      label: `Glaubhaftmachung Tätigkeit ${t("custom.common.federal-state")}`,
      value: "euAffirmation",
    },
    { label: "Sonstiges", value: "euOther" },
  ],
  RULE_APPROBATION: [
    {
      label: "Letzter Prüfungsort im Zuständigkeitsbereich",
      value: "regularMuenster",
    },
    { label: "Sonstiges", value: "regularOther" },
  ],
  THIRD_PARTY: [
    {
      label: "Stellenzusage / Hospitation oder Praktikum",
      value: "thirdJobOffer",
    },
    {
      label: `Dauerhafter Wohnsitz in ${t("custom.common.federal-state")}`,
      value: "thirdPermanentResidence",
    },
    { label: "Standortvermerk ZSBA", value: "thirdZsbaNote" },
    { label: "Sonstiges", value: "thirdOther" },
  ],
}

watch(isReasonFieldEnabled, (isEnabled) => {
  if (!isEnabled) {
    state.reason = undefined
  }
})

watch(
  () => state.proofOfResponsibility,
  (newValue) => {
    if (newValue === false) {
      state.providedProof = undefined
    }
  }
)
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Zuständigkeit"
      />
    </template>

    <IffContentSection
      headline="Zuständigkeit nachweisen"
      :is-last-child="true"
    >
      <IffDualRadioGroup
        legend="Kann ein Nachweis für die Zuständigkeit erbracht werden?"
        name="proofOfResponsibility"
        v-model="state.proofOfResponsibility"
        required
        aria-controls="proof-options-region reason-region"
      />

      <div id="proof-options-region" aria-live="polite">
        <IffTransitionHeight>
          <IffRadioGroup
            v-if="applicationType && state.proofOfResponsibility"
            legend="Nachweis durch"
            name="providedProof"
            v-model="state.providedProof"
            :options="providedProofOptionsByApplicationType[applicationType]"
            required
            aria-controls="reason-region"
          />
        </IffTransitionHeight>
      </div>

      <div id="reason-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="isReasonFieldEnabled"
            class="w-[600px]"
            name="reason"
            label="Begründung"
            :required="isReasonFieldEnabled"
            :help="`Noch ${
              REASON_MAX_LENGTH - (state.reason?.length ?? 0)
            } Zeichen verfügbar`"
          >
            <UTextarea
              v-model="state.reason"
              :maxlength="REASON_MAX_LENGTH"
              resize
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>

      <UFormGroup
        name="responsibilityDate"
        label="Zuständigkeit / festgestellt am"
        required
        class="w-[250px]"
      >
        <template #hint>
          <UButton
            @click="
              state.responsibilityDate = new Date().toISOString().slice(0, 10)
            "
            icon="i-ic-outline-access-time"
            size="sm"
            color="gray"
            variant="ghost"
            :trailing="false"
            aria-label="Aktuelles Datum wählen"
            title="Aktuelles Datum wählen"
          />
        </template>

        <UInput
          v-model="state.responsibilityDate"
          type="date"
          aria-required="true"
          class="whitespace-normal w-[150px]"
        />
      </UFormGroup>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
